import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { BILLING_RULES_KEY, useBillHaulHourly } from '../../hooks/useBillHaulHourly';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';

/*
 * The one rental billing rule that is read: `bill_haul_hourly` (rentals-haul
 * item 5, api#1054). On, every completed dispatch that names a truck is billed
 * on the rental invoice as hours × the price book's hourly haul rate; off, only
 * mob / demob fees bill a trip. Admin only (PATCH requireRole('admin')).
 *
 * The rest of rental_billing_defaults (frequency, anchor day, payment terms,
 * tax rate and label, three other switches) is still read by nothing, so it
 * stays hidden and this page never sends it. Wire a reader up before bringing a
 * field back; the old form is RentalsBillingRulesSection, unmounted.
 */
export default function RentalsSettingsPage() {
  const { hasVertical } = useAuth();
  const rentals = hasVertical('surface_rentals');
  const canWrite = useCanWriteCompanySettings();
  const queryClient = useQueryClient();
  const { on, query } = useBillHaulHourly(rentals);
  const failure = queryLoadFailure(query);

  const save = useMutation({
    mutationFn: (next: boolean) => api.rentals.settings.billingRules.update({ bill_haul_hourly: next }),
    onSuccess: (_r, next) => {
      queryClient.invalidateQueries({ queryKey: BILLING_RULES_KEY });
      queryClient.invalidateQueries({ queryKey: ['rentals-billing'] });
      toast.success(next ? 'Hauls are billed by the hour' : 'Hauls are not billed by the hour');
    },
    // The API's words.
    onError: (err) => toast.error(getApiErrorMessage(err, 'The setting was not saved.')),
  });

  if (!rentals) {
    return (
      <div className="min-h-full">
        <div className="px-6 py-12 text-center text-fieldiq-muted">Surface Rentals vertical is not enabled for this company.</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="max-w-2xl space-y-4 px-6 py-4">
        {!canWrite && <p className="text-xs text-fieldiq-muted">Only an admin can change rental billing settings.</p>}
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="rental billing settings" />
        ) : query.isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-fieldiq-accent" aria-label="Loading" /></div>
        ) : (
          <div className="flex items-center justify-between rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
            <div className="mr-4 flex-1">
              <p className="text-sm font-medium text-white">Bill hauls by the hour</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">Each delivery and pickup with a truck bills its hours at the price book's haul rate.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label="Bill hauls by the hour"
              disabled={!canWrite || save.isPending}
              onClick={() => save.mutate(!on)}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${on ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
